/**
 * Messages of the interaction kit (shortcut sheet, scope names, drag-and-drop announcements, undo
 * labels). The language follows the UI kit (`UiI18nProvider` / `setUiLocale` of `@dataroom/ui`), so the
 * app sets it once. `de` must have every key of `en` (checked by the type below). `{name}` placeholders
 * are interpolated; keys ending in `_one` / `_other` are chosen by the `count` variable.
 */
import { getUiLocale, useUiLocale } from '@dataroom/ui';
import { useCallback } from 'react';

const en = {
  'ix.sheet.title': 'Keyboard shortcuts',
  'ix.sheet.description': 'Shortcuts that work here. Single keys pause while you type in a field.',
  'ix.sheet.search': 'Search shortcuts',
  'ix.sheet.empty': 'No shortcut matches “{q}”.',
  'ix.sheet.then': 'then',
  'ix.sheet.or': 'or',
  'ix.sheet.unavailable': 'not available right now',
  'ix.sheet.footer': 'Press {key} anywhere to open this list.',
  'ix.scope.global': 'Everywhere',
  'ix.scope.route': 'This page',
  'ix.scope.editor': 'Editor',
  'ix.scope.grid': 'Grid',
  'ix.scope.list': 'List',
  'ix.scope.layer': 'This window',
  'ix.scope.tree': 'Tree',
  'ix.group.move': 'Move',
  'ix.cmd.shortcuts': 'Show keyboard shortcuts',
  'ix.cmd.undo': 'Undo',
  'ix.cmd.redo': 'Redo',
  'ix.cmd.copy': 'Copy',
  'ix.cmd.cut': 'Cut',
  'ix.cmd.paste': 'Paste',
  'ix.cmd.duplicate': 'Duplicate',
  'ix.cmd.selectAll': 'Select all',
  'ix.cmd.delete': 'Delete',
  'ix.cmd.escape': 'Deselect',
  'ix.cmd.nudgeUp': 'Nudge up',
  'ix.cmd.nudgeDown': 'Nudge down',
  'ix.cmd.nudgeLeft': 'Nudge left',
  'ix.cmd.nudgeRight': 'Nudge right',
  'ix.cmd.nudgeUpFar': 'Nudge up by 10',
  'ix.cmd.nudgeDownFar': 'Nudge down by 10',
  'ix.cmd.nudgeLeftFar': 'Nudge left by 10',
  'ix.cmd.nudgeRightFar': 'Nudge right by 10',
  'ix.cmd.moveUp': 'Move up',
  'ix.cmd.moveDown': 'Move down',
  'ix.cmd.moveTop': 'Move to top',
  'ix.cmd.moveBottom': 'Move to bottom',
  'ix.cmd.moveInto': 'Move into the item above',
  'ix.cmd.moveOut': 'Move out of its parent',
  'ix.undo.label': 'Undo {label}',
  'ix.redo.label': 'Redo {label}',
  'ix.undo.nothing': 'Nothing to undo',
  'ix.redo.nothing': 'Nothing to redo',
  'ix.undo.done': 'Undone',
  'ix.redo.done': 'Redone',
  'ix.undo.failed': 'Could not undo',
  'ix.undo.partial_one': 'Undone · {count} change was kept because someone changed it since',
  'ix.undo.partial_other': 'Undone · {count} changes were kept because someone changed them since',
  'ix.undo.expired': 'This change can no longer be undone',
  'ix.dnd.handle': 'Reorder {item}',
  'ix.dnd.handleHint': 'Press Space to pick up, arrow keys to move, Space to drop, Escape to cancel.',
  'ix.dnd.lifted': 'Picked up {item}. Position {position} of {total}.',
  'ix.dnd.moved': '{item}: position {position} of {total}.',
  'ix.dnd.dropped': '{item} dropped at position {position} of {total}.',
  'ix.dnd.cancelled': 'Reorder cancelled. {item} is back at position {position} of {total}.',
  'ix.dnd.place.root': 'at the top level',
  'ix.dnd.place.inside': 'inside {parent}',
  'ix.dnd.treeLifted': 'Picked up {item}, {place}, position {position}.',
  'ix.dnd.treeMoved': '{item}: {place}, position {position}.',
  'ix.dnd.treeDropped': '{item} dropped {place}, position {position}.',
  'ix.dnd.treeCancelled': 'Move cancelled. {item} is back {place}, position {position}.',
  'ix.dnd.treeDroppedMany_one': '{count} item dropped {place}, position {position}.',
  'ix.dnd.treeDroppedMany_other': '{count} items dropped {place}, position {position}.',
  'ix.dnd.dropFiles': 'Drop files to upload',
  'ix.dnd.dropFilesHere': 'Drop to add {count} files',
  'ix.dnd.fileRejected': '{name} is not an accepted file type',
  'ix.dirty.title': 'Leave without saving?',
  'ix.dirty.body': 'Your changes are not saved yet. If you leave now, they will be lost.',
  'ix.dirty.bodyNamed': 'Your changes to {label} are not saved yet. If you leave now, they will be lost.',
  'ix.dirty.leave': 'Leave without saving',
  'ix.dirty.stay': 'Keep editing',
  'ix.paste.failed_one': '{count} value could not be pasted',
  'ix.paste.failed_other': '{count} values could not be pasted',
  'ix.paste.foreign': 'Pasted from another workspace',
};

export type IxMessageKey = keyof typeof en;
type Messages = Record<IxMessageKey, string>;

const de: Messages = {
  'ix.sheet.title': 'Tastenkürzel',
  'ix.sheet.description':
    'Tastenkürzel, die hier funktionieren. Einzeltasten sind pausiert, während Sie in ein Feld tippen.',
  'ix.sheet.search': 'Tastenkürzel suchen',
  'ix.sheet.empty': 'Kein Tastenkürzel passt zu „{q}“.',
  'ix.sheet.then': 'dann',
  'ix.sheet.or': 'oder',
  'ix.sheet.unavailable': 'gerade nicht verfügbar',
  'ix.sheet.footer': 'Drücken Sie überall {key}, um diese Liste zu öffnen.',
  'ix.scope.global': 'Überall',
  'ix.scope.route': 'Diese Seite',
  'ix.scope.editor': 'Editor',
  'ix.scope.grid': 'Tabelle',
  'ix.scope.list': 'Liste',
  'ix.scope.layer': 'Dieses Fenster',
  'ix.scope.tree': 'Baumansicht',
  'ix.group.move': 'Verschieben',
  'ix.cmd.shortcuts': 'Tastenkürzel anzeigen',
  'ix.cmd.undo': 'Rückgängig',
  'ix.cmd.redo': 'Wiederholen',
  'ix.cmd.copy': 'Kopieren',
  'ix.cmd.cut': 'Ausschneiden',
  'ix.cmd.paste': 'Einfügen',
  'ix.cmd.duplicate': 'Duplizieren',
  'ix.cmd.selectAll': 'Alles auswählen',
  'ix.cmd.delete': 'Löschen',
  'ix.cmd.escape': 'Auswahl aufheben',
  'ix.cmd.nudgeUp': 'Nach oben verschieben',
  'ix.cmd.nudgeDown': 'Nach unten verschieben',
  'ix.cmd.nudgeLeft': 'Nach links verschieben',
  'ix.cmd.nudgeRight': 'Nach rechts verschieben',
  'ix.cmd.nudgeUpFar': 'Um 10 nach oben verschieben',
  'ix.cmd.nudgeDownFar': 'Um 10 nach unten verschieben',
  'ix.cmd.nudgeLeftFar': 'Um 10 nach links verschieben',
  'ix.cmd.nudgeRightFar': 'Um 10 nach rechts verschieben',
  'ix.cmd.moveUp': 'Nach oben',
  'ix.cmd.moveDown': 'Nach unten',
  'ix.cmd.moveTop': 'Ganz nach oben',
  'ix.cmd.moveBottom': 'Ganz nach unten',
  'ix.cmd.moveInto': 'In das Element darüber verschieben',
  'ix.cmd.moveOut': 'Aus dem übergeordneten Element herausnehmen',
  'ix.undo.label': '{label} rückgängig machen',
  'ix.redo.label': '{label} wiederholen',
  'ix.undo.nothing': 'Nichts rückgängig zu machen',
  'ix.redo.nothing': 'Nichts zu wiederholen',
  'ix.undo.done': 'Rückgängig gemacht',
  'ix.redo.done': 'Wiederholt',
  'ix.undo.failed': 'Rückgängig machen fehlgeschlagen',
  'ix.undo.partial_one':
    'Rückgängig gemacht · {count} Änderung blieb erhalten, weil sie inzwischen jemand geändert hat',
  'ix.undo.partial_other':
    'Rückgängig gemacht · {count} Änderungen blieben erhalten, weil sie inzwischen jemand geändert hat',
  'ix.undo.expired': 'Diese Änderung kann nicht mehr rückgängig gemacht werden',
  'ix.dnd.handle': '{item} verschieben',
  'ix.dnd.handleHint':
    'Leertaste zum Aufnehmen, Pfeiltasten zum Verschieben, Leertaste zum Ablegen, Escape zum Abbrechen.',
  'ix.dnd.lifted': '{item} aufgenommen. Position {position} von {total}.',
  'ix.dnd.moved': '{item}: Position {position} von {total}.',
  'ix.dnd.dropped': '{item} an Position {position} von {total} abgelegt.',
  'ix.dnd.cancelled': 'Verschieben abgebrochen. {item} ist wieder an Position {position} von {total}.',
  'ix.dnd.place.root': 'auf der obersten Ebene',
  'ix.dnd.place.inside': 'in {parent}',
  'ix.dnd.treeLifted': '{item} aufgenommen, {place}, Position {position}.',
  'ix.dnd.treeMoved': '{item}: {place}, Position {position}.',
  'ix.dnd.treeDropped': '{item} {place} abgelegt, Position {position}.',
  'ix.dnd.treeCancelled': 'Verschieben abgebrochen. {item} ist wieder {place}, Position {position}.',
  'ix.dnd.treeDroppedMany_one': '{count} Element {place} abgelegt, Position {position}.',
  'ix.dnd.treeDroppedMany_other': '{count} Elemente {place} abgelegt, Position {position}.',
  'ix.dnd.dropFiles': 'Dateien zum Hochladen ablegen',
  'ix.dnd.dropFilesHere': 'Ablegen, um {count} Dateien hinzuzufügen',
  'ix.dnd.fileRejected': '{name} ist kein erlaubter Dateityp',
  'ix.dirty.title': 'Ohne Speichern verlassen?',
  'ix.dirty.body':
    'Ihre Änderungen sind noch nicht gespeichert. Wenn Sie die Seite jetzt verlassen, gehen sie verloren.',
  'ix.dirty.bodyNamed':
    'Ihre Änderungen an {label} sind noch nicht gespeichert. Wenn Sie die Seite jetzt verlassen, gehen sie verloren.',
  'ix.dirty.leave': 'Ohne Speichern verlassen',
  'ix.dirty.stay': 'Weiter bearbeiten',
  'ix.paste.failed_one': '{count} Wert konnte nicht eingefügt werden',
  'ix.paste.failed_other': '{count} Werte konnten nicht eingefügt werden',
  'ix.paste.foreign': 'Aus einem anderen Arbeitsbereich eingefügt',
};

const catalogs: Record<string, Messages> = { en, de };

export type IxVars = Record<string, string | number>;
export type IxTranslate = (key: IxMessageKey, vars?: IxVars) => string;

/** Add or replace a language (a third locale, tests). */
export function registerInteractionMessages(locale: string, messages: Partial<Messages>): void {
  catalogs[locale] = { ...(catalogs[locale] ?? en), ...messages } as Messages;
}

export function translate(locale: string, key: IxMessageKey, vars?: IxVars): string {
  const catalog = catalogs[locale] ?? catalogs[locale.split('-')[0] ?? 'en'] ?? en;
  let k = key;
  if (vars && typeof vars.count === 'number') {
    const rule = new Intl.PluralRules(locale).select(vars.count) === 'one' ? 'one' : 'other';
    const plural = `${key.replace(/_(one|other)$/, '')}_${rule}`;
    if (plural in en) k = plural as IxMessageKey;
  }
  const text = catalog[k] ?? en[k];
  return vars
    ? text.replace(/\{(\w+)\}/g, (m, name: string) => (name in vars ? String(vars[name]) : m))
    : text;
}

/** Translate outside React (announcements, commands, toasts). */
export const ixT: IxTranslate = (key, vars) => translate(getUiLocale(), key, vars);

export function useIxT(): IxTranslate {
  const locale = useUiLocale();
  return useCallback((key, vars) => translate(locale, key, vars), [locale]);
}
